import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  OpenInNew as OpenInNewIcon,
  Article as ArticleIcon,
  Explore as GuideIcon,
  FormatListNumbered as ListIcon,
  Collections as CollectionIcon,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { contentApi, type EditorialListItem, type EditorialTemplate, type ContentStatus } from '../../services/api-client';
import { useAuth } from '../../contexts/GoogleAuthContext';
import ContentStatusChip from '../../components/content/ContentStatusChip';
import ContentNav from '../../components/content/ContentNav';

// Template icon mapping
const TEMPLATE_ICONS: Record<EditorialTemplate, React.ReactElement> = {
  article: <ArticleIcon fontSize="small" />,
  guide: <GuideIcon fontSize="small" />,
  list: <ListIcon fontSize="small" />,
  collection: <CollectionIcon fontSize="small" />,
};

const TEMPLATE_LABELS: Record<EditorialTemplate, string> = {
  article: 'Article',
  guide: 'Guide',
  list: 'List',
  collection: 'Collection',
};

type SortKey = 'title' | 'template' | 'status' | 'updatedAt';
type SortDir = 'asc' | 'desc';

export default function EditorialList() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Filters
  const [search, setSearch] = useState('');
  const [templateFilter, setTemplateFilter] = useState<EditorialTemplate | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<ContentStatus | 'all'>('all');
  const [sortKey, setSortKey] = useState<SortKey>('title');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Permissions
  const canEdit =
    user?.role === 'admin' || user?.effective_permissions?.content?.edit === true;

  // Fetch editorials
  const {
    data: editorials,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['content', 'editorials'],
    queryFn: () => contentApi.getEditorials(),
    staleTime: 2 * 60 * 1000,
  });

  // Sort handler
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  // Filter + sort
  const filtered = useMemo(() => {
    if (!editorials) return [];
    let items = [...editorials];

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(
        (e) =>
          e.title.toLowerCase().includes(q) ||
          e.subtitle?.toLowerCase().includes(q) ||
          e.slug.toLowerCase().includes(q) ||
          e.tags?.some((t) => t.toLowerCase().includes(q))
      );
    }

    // Template filter
    if (templateFilter !== 'all') {
      items = items.filter((e) => e.template === templateFilter);
    }

    // Status filter
    if (statusFilter !== 'all') {
      items = items.filter((e) => (e.status ?? 'published') === statusFilter);
    }

    // Sort
    items.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'title':
          cmp = a.title.localeCompare(b.title);
          break;
        case 'template':
          cmp = (a.template ?? '').localeCompare(b.template ?? '');
          break;
        case 'status':
          cmp = (a.status ?? 'published').localeCompare(b.status ?? 'published');
          break;
        case 'updatedAt':
          cmp =
            new Date(a.updatedAt || 0).getTime() -
            new Date(b.updatedAt || 0).getTime();
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return items;
  }, [editorials, search, templateFilter, statusFilter, sortKey, sortDir]);

  // Stats
  const stats = useMemo(() => {
    if (!editorials) return { total: 0, published: 0, drafts: 0 };
    return {
      total: editorials.length,
      published: editorials.filter((e) => (e.status ?? 'published') === 'published').length,
      drafts: editorials.filter((e) => (e.status ?? 'published') === 'draft').length,
    };
  }, [editorials]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load editorials. Please check that the Content API is reachable.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Content section tabs */}
      <ContentNav />

      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Editorial Articles
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {stats.total} articles &middot; {stats.published} published &middot;{' '}
            {stats.drafts} drafts
          </Typography>
        </Box>
        {canEdit && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/content/cms/editorials/new')}
          >
            New Article
          </Button>
        )}
      </Box>

      {/* Filters Row */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search articles..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Template</InputLabel>
          <Select
            value={templateFilter}
            label="Template"
            onChange={(e) => setTemplateFilter(e.target.value as EditorialTemplate | 'all')}
          >
            <MenuItem value="all">All Templates</MenuItem>
            <MenuItem value="article">Article</MenuItem>
            <MenuItem value="guide">Guide</MenuItem>
            <MenuItem value="list">List</MenuItem>
            <MenuItem value="collection">Collection</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value as ContentStatus | 'all')}
          >
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="in_review">In Review</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="scheduled">Scheduled</MenuItem>
            <MenuItem value="published">Published</MenuItem>
          </Select>
        </FormControl>

        {(search || templateFilter !== 'all' || statusFilter !== 'all') && (
          <Chip
            label="Clear filters"
            size="small"
            onDelete={() => {
              setSearch('');
              setTemplateFilter('all');
              setStatusFilter('all');
            }}
          />
        )}
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 48 }} />
              <TableCell>
                <TableSortLabel
                  active={sortKey === 'title'}
                  direction={sortKey === 'title' ? sortDir : 'asc'}
                  onClick={() => handleSort('title')}
                >
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortKey === 'template'}
                  direction={sortKey === 'template' ? sortDir : 'asc'}
                  onClick={() => handleSort('template')}
                >
                  Template
                </TableSortLabel>
              </TableCell>
              <TableCell>Tags</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortKey === 'status'}
                  direction={sortKey === 'status' ? sortDir : 'asc'}
                  onClick={() => handleSort('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>Author</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {search || templateFilter !== 'all' || statusFilter !== 'all'
                      ? 'No articles match your filters.'
                      : 'No editorial articles found.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {filtered.map((article) => (
              <TableRow
                key={article.slug}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/content/cms/editorials/${article.slug}`)}
              >
                {/* Thumbnail */}
                <TableCell sx={{ pr: 0 }}>
                  <Avatar
                    variant="rounded"
                    src={article.thumbnailImage || article.heroImage}
                    alt={article.title}
                    sx={{ width: 40, height: 40 }}
                  >
                    {TEMPLATE_ICONS[article.template] ?? <ArticleIcon />}
                  </Avatar>
                </TableCell>

                {/* Title + subtitle */}
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                    {article.title}
                  </Typography>
                  {article.subtitle && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {article.subtitle}
                    </Typography>
                  )}
                </TableCell>

                {/* Template */}
                <TableCell>
                  <Chip
                    icon={TEMPLATE_ICONS[article.template]}
                    label={TEMPLATE_LABELS[article.template] ?? article.template}
                    size="small"
                    variant="outlined"
                    sx={{ textTransform: 'capitalize' }}
                  />
                </TableCell>

                {/* Tags */}
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {article.tags?.slice(0, 2).map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    ))}
                    {(article.tags?.length ?? 0) > 2 && (
                      <Chip
                        label={`+${article.tags!.length - 2}`}
                        size="small"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    )}
                  </Box>
                </TableCell>

                {/* Status */}
                <TableCell>
                  <ContentStatusChip status={article.status ?? 'published'} />
                </TableCell>

                {/* Author */}
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {article.author || '-'}
                  </Typography>
                </TableCell>

                {/* Actions */}
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                  {canEdit && (
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/content/cms/editorials/${article.slug}`)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="View on site">
                    <IconButton
                      size="small"
                      component="a"
                      href={`https://curatedby.tripsby.ai/blog/${article.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer summary */}
      <Box sx={{ mt: 1, px: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Showing {filtered.length} of {stats.total} articles
        </Typography>
      </Box>
    </Box>
  );
}
